/**
 * @module utils/window-events
 */
import throttle from "lodash/throttle";
import ee from "lib/eventemitter";

// cache body
const body = window.document.body;

/**
 * @method handleVisibilityChange
 */
function handleVisibilityChange() {
  if (document.hidden || document.webkitHidden) {
    ee.trigger("document.hidden", [true]);
    return;
  }
  ee.trigger("document.hidden", [false]);
}

/**
 * @method pageYOffset
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pageYOffset
 * @returns {Number}
 */
export function pageYOffset() {
  // window.scrollY is not supported in IE11, but window.pageYOffset should contain the same value.
  // window.document.body.scrollTop is always 0 for some reason, so we're using window.pageYOffset instead.
  return window.pageYOffset || document.documentElement.scrollTop || body.scrollTop;
}

function onWindowScroll() {
  ee.trigger("windowScroll.throttled", [pageYOffset()]);
}

function onWindowResize() {
  ee.trigger("windowResize.throttled", [body.getBoundingClientRect()]);
}

function onMouseMove() {
  ee.trigger("mouseMove.throttled");
}

/**
 * @method register
 * @description Attaches throttled scroll and resize listeners to the window object
 */
export function register() {
  window.addEventListener("scroll", throttle(onWindowScroll, 150), { capture: true, passive: true });
  window.addEventListener("resize", throttle(onWindowResize, 50), { passive: true });
  window.addEventListener("mousemove", throttle(onMouseMove, 1500), { passive: true });
  document.addEventListener("visibilitychange", handleVisibilityChange);
  document.addEventListener("webkitvisibilitychange", handleVisibilityChange);
}

/**
 * @method unregister
 * @description Removes event listeners
 *
  export function unregister() {
    window.removeEventListener("scroll", onWindowScroll);
    window.removeEventListener("resize", onWindowResize);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("webkitvisibilitychange", handleVisibilityChange);
  }
};
*/

/**
 * @method windowScrollTo
 * @description Wrapper for `window.ScrollTo()`, with feature detection for `ScrollToOptions` param.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollTo
 * @param {Object} c - collection of params
 * @param {Number} [c.top = 0] - Number of pixels along the Y axis to scroll the window or element.
 * @param {Number} [c.left = 0] - Number of pixels along the X axis to scroll the window or element.
 * @param {String} [c.behavior="smooth"] - Whether the scrolling should animate smoothly, or happen instantly in a single jump.
 */
export function windowScrollTo({ top = 0, left = 0, behavior = "smooth" } = {}) {
  if (!window.scrollTo) {
    return; // All browsers should support, but just in case.
  }
  // Feature detection: https://hospodarets.com/native_smooth_scrolling
  if ("scrollBehavior" in document.documentElement.style) {
    window.scrollTo({ top, left, behavior });
  } else {
    window.scrollTo(left, top); // x, y
  }
}
